/**
 * Route guard factory: gates a route on a single org permission.
 *
 * Usage:
 * ```ts
 * { path: 'settings/api-keys',
 *   canActivate: [orgPermissionGuard('apiKeys:read')], … }
 * ```
 *
 * Internal admins bypass the check. Unauthenticated users are sent to
 * `/login`; authenticated-but-unauthorized users are sent to `/`.
 */

import { CanActivateFn, Router } from '@angular/router';
import { effect, inject, Injector, runInInjectionContext } from '@angular/core';

import { AuthService } from '../firebase/auth.service';
import { CATCH_UP_MS, MembershipSessionService } from './membership-session.service';
import { OrgService } from './org.service';
import type { OrgPermission } from './orgs.types';

export function orgPermissionGuard(perm: OrgPermission): CanActivateFn {
  return async (_route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const org = inject(OrgService);
    const membership = inject(MembershipSessionService);
    const injector = inject(Injector);

    await waitForSignal(() => auth.claimsInitialized(), injector);

    if (!auth.isSignedIn()) {
      return router.createUrlTree(['/login'], {
        queryParams: state.url ? { returnUrl: state.url } : undefined,
      });
    }
    if (auth.isInternalAdmin()) return true;

    // Wait for the org service to resolve the active org (one user-doc
    // round-trip after sign-in). If the snapshots are slow to arrive we
    // give them a generous window before falling through to the deny
    // path; without this, a slow Firestore round-trip would silently
    // redirect a legitimately authorized user to `/`.
    const orgReady = await waitForSignal(() => org.activeOrgId() !== null, injector, 8_000);
    if (!orgReady) return router.createUrlTree(['/']);

    if (org.can(perm)) return true;
    // No role in the token. It may be stale: a person just added to this org
    // (a fresh invite deep-linking here) has it in users/{uid}.orgIds before
    // the token says so. Wait, bounded, for MembershipSessionService to bring
    // the token in line, then decide. An in-step token is settled already, so
    // a real denial costs nothing, and a granted role never waits.
    await waitForSignal(() => membership.settled(), injector, CATCH_UP_MS);
    if (org.can(perm)) return true;
    return router.createUrlTree(['/']);
  };
}

/**
 * Resolve once `predicate()` is true, or return false on timeout. Always
 * tears down the underlying `effect()` watcher (including on timeout)
 * so the guard never leaks effects for users whose org context never
 * resolves (e.g. signed-in but org-less).
 */
function waitForSignal(
  predicate: () => boolean,
  injector: Injector,
  timeoutMs = 5_000,
): Promise<boolean> {
  if (predicate()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let ref: { destroy(): void } | null = null;
    const timer = setTimeout(() => {
      ref?.destroy();
      resolve(false);
    }, timeoutMs);
    runInInjectionContext(injector, () => {
      ref = effect(() => {
        if (predicate()) {
          clearTimeout(timer);
          ref?.destroy();
          resolve(true);
        }
      });
    });
  });
}
